/**
 * Getting a returning visitor onto the build that is actually deployed.
 *
 * A refresh does not fetch this app. It is a PWA, so the service worker
 * answers the navigation out of its own precache, and the copy of the shell in
 * there is whatever was current when the worker installed. The worker
 * `vite-plugin-pwa` builds in `autoUpdate` mode calls `skipWaiting()` and
 * `clientsClaim()`, so a new one installs and takes over as soon as it is
 * found — but the page that found it was already drawn from the old one. The
 * sequence for a returning visitor is therefore: refresh, and get the old
 * build while the new worker quietly installs behind it; refresh again, and
 * get the new one. Which is precisely the "I had to hard-refresh" report,
 * Ctrl+Shift+R being the one gesture that bypasses the worker altogether.
 *
 * It is worse than merely stale. `cleanupOutdatedCaches` deletes the old
 * precache as the new worker activates, and the old hashed assets are gone
 * from the server too, so the page left on screen can no longer load a chunk
 * it happens not to have needed yet — the PDF exporter, say.
 *
 * So: when a new worker takes control, reload the page. That turns one refresh
 * into the whole update, which is what a refresh is for.
 *
 * Two things this must not do. It must not reload on the very first visit,
 * where the worker taking control is the one that drew the page and there is
 * nothing newer to go to. And it must not reload out from under unsaved work:
 * the app guards unload when the document is dirty, and a reload that trips
 * the app's own "leave site?" prompt is worse than a wait.
 */

/** How often a tab left open asks whether it has been overtaken. */
export const UPDATE_CHECK_MS = 30 * 60 * 1000;

export interface UpdateOptions {
  /**
   * Whether the page may be reloaded now. Asked at the moment the new worker
   * takes over, and again whenever the tab is brought back to the front, so an
   * update found during unsaved work is taken as soon as it is safe to take.
   */
  readonly canReload?: () => boolean;
  readonly reload?: () => void;
  readonly checkEveryMs?: number;
  /** The container to watch. Defaults to this browser's, where it has one. */
  readonly container?: ServiceWorkerContainer;
}

const NOTHING_TO_STOP = (): void => undefined;

/**
 * Watch for a newer build and reload onto it. Returns a stop function.
 *
 * A no-op in anything without service workers — an insecure origin, a browser
 * with them switched off, jsdom — where there is no worker to serve a stale
 * shell and so nothing to correct.
 */
export function watchForUpdates(options: UpdateOptions = {}): () => void {
  const container = options.container ?? browserContainer();
  if (container === undefined) return NOTHING_TO_STOP;

  const canReload = options.canReload ?? ((): boolean => true);
  const reload = options.reload ?? ((): void => window.location.reload());
  const checkEveryMs = options.checkEveryMs ?? UPDATE_CHECK_MS;

  // Whether a worker was already serving this page when it loaded. If not,
  // the one that claims it in a moment is the one that drew it.
  const wasControlled = container.controller !== null;
  let overtaken = false;
  let gone = false;

  const goIfSafe = (): void => {
    if (!overtaken || gone || !canReload()) return;
    gone = true;
    reload();
  };

  const check = async (): Promise<void> => {
    try {
      const registration = await container.getRegistration();
      await registration?.update();
    } catch {
      // Offline, or the registration has been cleared. The next check will do.
    }
  };

  const onControllerChange = (): void => {
    if (!wasControlled) return;
    overtaken = true;
    goIfSafe();
  };

  const onVisible = (): void => {
    if (document.visibilityState !== 'visible') return;
    // Back at the tab: a good moment both to take an update that was waiting
    // on unsaved work, and to ask for one, since the browser only checks the
    // worker script on navigation and this tab may not have navigated in days.
    goIfSafe();
    void check();
  };

  container.addEventListener('controllerchange', onControllerChange);
  document.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(() => void check(), checkEveryMs);

  return () => {
    container.removeEventListener('controllerchange', onControllerChange);
    document.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}

function browserContainer(): ServiceWorkerContainer | undefined {
  if (typeof navigator === 'undefined') return undefined;
  return (navigator as Navigator & { serviceWorker?: ServiceWorkerContainer }).serviceWorker;
}
